// MediaPipe FaceLandmarker singleton.
// First call downloads the model (~3MB) and the WASM runtime; subsequent
// calls return the cached instance. Capture phase awaits ensure() before
// processing video frames.
//
// Doc: docs/architecture.md (FE face/emotion tech-stack row) and
// docs/fe-state-machine.md#boot ("Initialize MediaPipe FaceLandmarker").

import { FilesetResolver, FaceLandmarker } from "@mediapipe/tasks-vision";

// Pinned to the version in package.json — bump both together.
const WASM_BASE = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm";
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";

let instance: FaceLandmarker | null = null;
let pending: Promise<FaceLandmarker> | null = null;

export function ensureLandmarker(): Promise<FaceLandmarker> {
  if (instance) return Promise.resolve(instance);
  if (pending) return pending;
  pending = (async () => {
    const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
    const lm = await FaceLandmarker.createFromOptions(vision, {
      baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
      outputFaceBlendshapes: true,
      // Head-pose (yaw/pitch/roll) for the "look at the camera" framing gate.
      // Decomposed in capture/head-pose.ts.
      outputFacialTransformationMatrixes: true,
      runningMode: "VIDEO",
      numFaces: 1,
    });
    instance = lm;
    return lm;
  })();
  return pending;
}

export function getLandmarker(): FaceLandmarker | null {
  return instance;
}
